import styled, { keyframes } from "styled-components";
import { mobileAuthSideContainer, mobileAuthMainContainer } from "../../styles/authShellMixins";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ---------- Layout estrutural (mesmo padrão do ViewAlerts) ----------

export const MainContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  min-height: 100vh;
  height: 100%;
  background: var(--app-bg-gradient);
  background-size: 400% 400%;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  overflow-x: hidden;

  ${mobileAuthMainContainer}
`;

export const SideContainer = styled.div`
  scrollbar-width: none;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  height: 100vh;
  min-height: 100vh;
  width: ${({ collapsed }) => (collapsed ? "calc(100% - 6vw)" : "calc(100% - 15vw)")};
  margin: 0;
  transition: margin-left 0.5s ease-in-out, width 0.5s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    display: none;
  }

  ${mobileAuthSideContainer}
`;

export const PageContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: none;
  box-sizing: border-box;
  padding: 0.75rem 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  flex: 1;
  min-height: 0;
`;

export const InnerContainer = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1400px;
  padding: 2rem;
`;

export const HeaderSection = styled.header`
  text-align: center;
  width: 100%;
  margin-bottom: 0.25rem;
`;

export const Title = styled.h2`
  font-size: 1.65rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0.5rem 0 0.35rem 0;
  text-shadow: 0 4px 20px color-mix(in srgb, var(--accent-secondary) 30%, transparent);
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: 0.1s;
  animation-fill-mode: both;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
`;

export const Subtitle = styled.p`
  font-size: 0.95rem;
  font-weight: 400;
  color: var(--text-secondary);
  margin: 0 auto;
  line-height: 1.45;
  text-align: center;
  max-width: 42rem;
`;

export const LoadingContainer = styled.div`
  width: 100%;
  min-height: 40vh;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
`;

export const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-subtle);
  border-top-color: var(--accent-secondary);
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const EmptyMessage = styled.div`
  padding: 1.25rem 0.5rem;
  color: ${({ $error }) => ($error ? "var(--danger)" : "var(--text-secondary)")};
  font-size: 0.95rem;
  line-height: 1.5;
  text-align: left;
`;

// ---------- Tabela de versões ----------

export const TableContainer = styled.div`
  width: 100%;
  max-width: none;
  flex: 1;
  min-height: 0;
  max-height: calc(100vh - 140px);
  overflow-x: auto;
  overflow-y: auto;
  margin: 0.5rem 0 0;
  align-self: stretch;
  background: var(--surface-glass);
  backdrop-filter: blur(20px);
  border: 1px solid var(--accent-secondary-soft);
  border-radius: 16px;
  padding: 1rem 1rem 1.25rem;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--surface-overlay);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const Thead = styled.thead`
  background: var(--table-header-bg);

  th {
    text-align: left;
    padding: 0.75rem 1rem;
    color: var(--table-header-color);
    font-size: var(--table-header-font-size);
    font-weight: var(--table-header-font-weight);
    text-transform: uppercase;
    letter-spacing: var(--table-header-letter-spacing);
    border-bottom: var(--table-header-border);
  }
`;

export const Tr = styled.tr`
  &:hover {
    background: var(--surface-overlay);
  }
`;

export const Td = styled.td`
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--border-subtle);
  color: var(--text-primary);
  font-size: 0.9rem;
`;

export const Badge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ $channel }) =>
    $channel === "store" ? "var(--accent-soft)" : "var(--surface-overlay)"};
  color: ${({ $channel }) =>
    $channel === "store" ? "var(--accent-secondary)" : "var(--text-secondary)"};
`;

// ---------- Formulário (VersionForm) ----------

export const FormCard = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 0.5rem 0.25rem 0.25rem;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
  color: var(--text-primary);
  border: none;
  box-sizing: border-box;

  h2 {
    color: var(--text-heading);
    margin: 0.25rem 2rem 1rem 0;
    font-size: 1.25rem;
  }
`;

export const ListHeaderRow = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;

  h1 {
    text-align: center;
  }
`;

export const NewVersionButtonWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;

export const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 640px;
  width: 100%;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const FieldRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  > * {
    flex: 1;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

export const Label = styled.label`
  font-weight: 600;
  color: var(--text-heading);
`;

export const ShowInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface-2);
  color: var(--text-primary);
  width: 100%;
`;

export const StyledSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface-2);
  color: var(--text-primary);
  width: 100%;
`;

export const ShowTextarea = styled.textarea`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface-2);
  color: var(--text-primary);
  width: 100%;
  min-height: 80px;
  resize: vertical;
`;

export const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

export const StyledButton = styled.button`
  background: ${props => props.secondary
    ? 'var(--input-bg)'
    : 'var(--accent-secondary-soft)'};
  border: 2px solid ${props => props.secondary
    ? 'var(--border-subtle)'
    : 'var(--border-default)'};
  border-radius: 16px;
  color: ${props => props.secondary
    ? 'var(--text-primary)'
    : 'var(--text-heading)'};
  cursor: pointer;
  padding: 12px 24px;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  min-width: 140px;
  min-height: 44px;
  z-index: 2;
  marginLeft: "auto"

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      ${props => props.secondary
    ? 'var(--input-bg) 0%, var(--surface-glass) 100%'
    : 'var(--accent-secondary-soft) 0%, var(--accent-soft) 100%'}
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 14px;
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 14px;
    background: transparent;
    box-shadow: ${props => props.secondary
    ? 'var(--shadow-sm)'
    : '0 6px 15px var(--accent-soft)'};
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: ${props => props.secondary
    ? 'var(--text-muted)'
    : 'var(--accent)'};
    color: var(--text-primary);

    &::before {
      opacity: 1;
    }

    &::after {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media screen and (max-width: 768px) {
    min-width: 120px;
    padding: 10px 20px;
  }
`;

export const HelpText = styled.small`
  color: var(--text-muted);
`;

export const ProgressBarTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: var(--surface-2);
  overflow: hidden;
  margin-top: 0.5rem;
`;

export const ProgressBarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: var(--accent);
  width: ${({ $percent }) => $percent}%;
  transition: width 0.2s ease;
`;

// ---------- Modal de detalhes (VersionDetailsModal) ----------

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: color-mix(in srgb, var(--brand-navy, #00212d) 52%, transparent);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10050;
  padding: 1rem;
`;

export const DetailsCard = styled.div`
  position: relative;
  width: 440px;
  max-width: 92vw;
  max-height: min(88vh, 720px);
  padding: 1.5rem 1.5rem 1.35rem;
  border-radius: 18px;
  box-shadow: var(--shadow-lg);
  background: var(--surface-1);
  color: var(--text-primary);
  border: 1px solid var(--border-subtle);
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--brand-cta-gradient-135);
  }

  h2 {
    color: var(--text-heading);
    margin: 0.35rem 2.25rem 1rem 0;
    font-size: 1.2rem;
    line-height: 1.3;
  }
`;

export const DetailsCloseButton = styled.button`
  position: absolute;
  top: 0.85rem;
  right: 0.85rem;
  background: var(--surface-2);
  border: 1px solid var(--border-default);
  color: var(--text-secondary);
  font-size: 1.25rem;
  cursor: pointer;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  line-height: 1;
  z-index: 2;

  &:hover {
    color: var(--text-heading);
    border-color: var(--accent);
  }
`;

export const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const DetailLabel = styled.span`
  font-weight: 600;
  color: var(--text-heading);
  font-size: 0.85rem;
`;

export const DetailLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--accent);
  text-decoration: underline;
  word-break: break-all;

  &:hover {
    color: var(--accent-secondary);
  }
`;

export const DetailEmpty = styled.span`
  color: var(--text-muted);
  font-size: 0.9rem;
`;

// ---------- Tooltip de ajuda ("?") ----------

export const InfoIconWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover > div,
  &:focus-within > div {
    opacity: 1;
    visibility: visible;
  }
`;

export const InfoIcon = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--surface-overlay);
  color: var(--text-secondary);
  font-size: 0.7rem;
  font-weight: 700;
  cursor: help;
  line-height: 1;

  &:hover,
  &:focus-visible {
    background: var(--accent);
    color: var(--text-on-accent);
  }
`;

export const InfoTooltipBox = styled.div`
  position: absolute;
  min-width: 220px;
  max-width: 280px;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  background: var(--surface-3, #1a1a1a);
  color: var(--text-primary);
  font-size: 0.78rem;
  font-weight: 400;
  line-height: 1.4;
  white-space: pre-line; 
  text-transform: none;
  letter-spacing: normal;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-subtle);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease;
  z-index: 20;

  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: var(--surface-3, #1a1a1a);
  }

  &[data-position="right"] {
    bottom: auto;
    top: 50%;
    left: calc(100% + 10px);
    transform: translateY(-50%);

    &::after {
      top: 50%;
      left: auto;
      right: 100%;
      transform: translateY(-50%);
      border-top-color: transparent;
      border-right-color: var(--surface-3, #1a1a1a);
    }
  }
`;

export const BackgroundPattern = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  background-image:
    radial-gradient(circle at 15% 20%, color-mix(in srgb, var(--accent-secondary) 12%, transparent) 0%, transparent 45%),
    radial-gradient(circle at 85% 80%, color-mix(in srgb, var(--accent) 12%, transparent) 0%, transparent 45%);
`;

export const ButtonRow = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
`;

export const ButtonContainerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const ButtonContainerRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const StyledTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  backdrop-filter: blur(20px);
  background: var(--surface-2);
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid var(--border-subtle);
`;

export const StyledTableHead = styled.thead`
  background: var(--table-header-bg);
`;

export const StyledTableRow = styled.tr`
  border-bottom: 1px solid var(--border-subtle);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--text-primary);
  height: 100%;
  width: 100%;

  &:last-child {
    border-bottom: none;
  }
`;

export const StyledTableCell = styled.td`
  padding: 10px 10px;
  vertical-align: top;
  color: var(--text-primary);
  font-size: 0.8125rem;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: anywhere;

  &:nth-child(1) {
    text-align: left;
    width: 10%;
    padding: 10px 20px;
  }
  &:nth-child(2) {
    text-align: center;
    width: 10%;
    padding: 10px 20px;
  }
  &:nth-child(3) {
    text-align: center;
    width: 10%;
    padding: 10px 20px;
  }
  &:nth-child(4) {
    text-align: center;
    width: 10%;
    padding: 10px 20px;
  }
  &:nth-child(5) {
    text-align: left;
    width: 27%;
  }
  &:nth-child(6) {
    text-align: center;
    width: 11%;
  }
  &:nth-child(7) {
    text-align: center;
    width: 11%;
    white-space: nowrap;
  }
  &:nth-child(8) {
    text-align: center;
    width: 11%;
    min-width: 108px;
    vertical-align: middle;
    overflow-wrap: normal;
    word-break: normal;
  }
`;

export const StyledTableHeader = styled.th`
  padding: 11px 10px;
  text-align: center;
  font-weight: var(--table-header-font-weight);
  color: var(--table-header-color);
  font-size: var(--table-header-font-size);
  text-transform: uppercase;
  letter-spacing: var(--table-header-letter-spacing);
  backdrop-filter: blur(10px);
  vertical-align: bottom;
  line-height: 1.25;
  word-wrap: break-word;

  &:nth-child(1) {
    text-align: left;
    width: 10%;
    padding: 11px 20px;
  }
  &:nth-child(2) {
    text-align: center;
    width: 10%;
    padding: 11px 20px;
  }
  &:nth-child(3) {
    text-align: center;
    width: 10%;
    padding: 11px 20px;
  }
  &:nth-child(4) {
    text-align: center;
    width: 10%;
    padding: 11px 20px;
  }
  &:nth-child(5) {
    text-align: left;
    width: 27%;
  }
  &:nth-child(6) {
    text-align: center;
    width: 11%;
  }
  &:nth-child(7) {
    text-align: center;
    width: 11%;
  }
  &:nth-child(8) {
    text-align: center;
    width: 11%;
    min-width: 108px;
  }
`;

export const StyledButtonVisualizar = styled.button`
  background: var(--accent-soft);
  border: 2px solid var(--border-default);
  border-radius: 20px;
  color: var(--text-heading);
  cursor: pointer;
  display: inline-block;
  font-family: -apple-system, system-ui, Roboto, sans-serif;
  padding: 8px 16px;
  text-align: center;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 11px;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  width: auto;
  min-width: 80px;
  height: 32px;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, var(--accent-secondary-soft) 0%, var(--accent-soft) 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 18px;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 18px;
    background: transparent;
    box-shadow: 0 8px 20px var(--accent-soft);
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-4px);
    border-color: var(--accent);
    color: var(--text-primary);

    &::before {
      opacity: 1;
    }

    &::after {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(-2px);
  }

  @media screen and (max-width: 768px) {
    font-size: 10px;
    padding: 6px 12px;
    min-width: 70px;
    height: 28px;
  }
`;